import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { custResetInfo, custResetPassword } from "../lib/api";
import Seo from "../components/Seo";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [state, setState] = useState("checking"); // checking | valid | invalid
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    custResetInfo(token).then((d) => { setInfo(d); setState("valid"); }).catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    if (password !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setSaving(true);
    try {
      const { token: jwt } = await custResetPassword(token, password);
      localStorage.setItem("fp_customer_token", jwt);
      toast.success("Contraseña guardada. ¡Bienvenido!");
      window.location.href = "/mi-cuenta";
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar la contraseña");
    } finally { setSaving(false); }
  };

  const isActivation = info?.kind === "activation";

  return (
    <div className="max-w-md mx-auto px-4 py-14" data-testid="set-password-page">
      <Seo title="Establecer contraseña | FABRIPAN" description="Cree su contraseña de acceso a FABRIPAN." noindex />
      {state === "checking" && (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      )}
      {state === "invalid" && (
        <div className="border border-border rounded-sm bg-white p-6 text-center space-y-3" data-testid="reset-invalid">
          <h1 className="font-heading font-bold text-2xl tracking-tighter">Enlace no válido</h1>
          <p className="text-sm text-muted-foreground">Este enlace ha caducado o ya se ha utilizado. Solicite uno nuevo desde la página de acceso.</p>
          <Link to="/acceso" className="inline-block bg-brand text-white px-5 py-2.5 rounded-sm text-sm font-semibold hover:bg-brand-dark">Ir a acceso</Link>
        </div>
      )}
      {state === "valid" && (
        <form onSubmit={submit} className="border border-border rounded-sm bg-white p-6 space-y-4">
          <div className="flex items-center gap-2 font-heading font-bold text-2xl tracking-tighter">
            <KeyRound className="h-6 w-6 text-brand" /> {isActivation ? "Active su cuenta" : "Nueva contraseña"}
          </div>
          <p className="text-sm text-muted-foreground">{isActivation ? "Cree una contraseña para " : "Elija una nueva contraseña para "}<strong>{info.email}</strong>.</p>
          <input required type="password" placeholder="Nueva contraseña" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="new-password" />
          <input required type="password" placeholder="Repetir contraseña" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="confirm-password" />
          <button type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-semibold py-3 rounded-sm transition-colors" data-testid="set-password-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><CheckCircle2 className="h-4 w-4" /> {isActivation ? "Activar cuenta" : "Guardar contraseña"}</>)}
          </button>
        </form>
      )}
    </div>
  );
}
